"use client";

import { ReactNode, useEffect, useState } from "react";
import { useRouter, usePathname } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@/store/auth-store";
import { useActiveClientStore } from "@/store/active-client-store";
import { Menu, Bell, LayoutDashboard, ClipboardList, LayoutGrid, Calendar, Users } from "lucide-react";
import { AppSidebar } from "@/components/layout/app-sidebar";
import { WhatsappFloatingInbox } from "@/modules/whatsapp/components/whatsapp-floating-inbox";
import { apiClient } from "@/lib/api-client";
import { branding } from "@/config/branding";

const BOTTOM_NAV = [
  { href: "/", label: "Início", Icon: LayoutDashboard, exact: true },
  { href: "/ordens", label: "OS", Icon: ClipboardList, exact: false },
  { href: "/pistas", label: "Pistas", Icon: LayoutGrid, exact: false },
  { href: "/servicos/agenda", label: "Agenda", Icon: Calendar, exact: false },
  { href: "/cadastro/clientes", label: "Clientes", Icon: Users, exact: false },
];

const desktopMediaQuery = "(min-width: 1024px)";
const sidebarCollapsedStorageKey = "garage-sidebar-collapsed";
const sidebarDesktopOpenStorageKey = "garage-sidebar-open-desktop";

const readStoredBoolean = (key: string) => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(key);
    if (raw === null) return null;
    return raw === "true";
  } catch {
    return null;
  }
};

const persistStoredBoolean = (key: string, value: boolean) => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.sessionStorage.setItem(key, String(value));
  } catch {
    /* ignore persistence errors */
  }
};

type CompanyBranding = { name: string; logo: string | null };

export default function AppLayout({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const { user, isHydrated } = useAuthStore();
  const { initialize: initializeActiveClient } = useActiveClientStore();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(
    () => readStoredBoolean(sidebarCollapsedStorageKey) ?? false,
  );

  const brandingQuery = useQuery({
    queryKey: ["company-branding", user?.company?.id ?? null],
    enabled: Boolean(user?.company?.id),
    queryFn: async () => {
      const res = await apiClient.get<CompanyBranding>("/branding");
      return res.data;
    },
  });
  const resolvedLogo = brandingQuery.data?.logo?.trim() || branding.logo;
  const resolvedName = brandingQuery.data?.name?.trim() || branding.name;

  useEffect(() => {
    if (isHydrated && !user) {
      router.replace("/login");
    }
  }, [isHydrated, router, user]);

  useEffect(() => {
    initializeActiveClient();
  }, [initializeActiveClient]);

  useEffect(() => {
    persistStoredBoolean(sidebarCollapsedStorageKey, isCollapsed);
  }, [isCollapsed]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const mediaQuery = window.matchMedia(desktopMediaQuery);

    const syncViewport = (matches: boolean) => {
      setIsDesktop(matches);
      if (matches) {
        const storedOpen = readStoredBoolean(sidebarDesktopOpenStorageKey);
        setIsSidebarOpen(storedOpen ?? true);
      } else {
        setIsSidebarOpen(false);
      }
    };

    syncViewport(mediaQuery.matches);

    const handleViewportChange = (event: MediaQueryListEvent) =>
      syncViewport(event.matches);

    mediaQuery.addEventListener("change", handleViewportChange);

    return () => mediaQuery.removeEventListener("change", handleViewportChange);
  }, []);

  useEffect(() => {
    if (!isDesktop) return;
    persistStoredBoolean(sidebarDesktopOpenStorageKey, isSidebarOpen);
  }, [isDesktop, isSidebarOpen]);

  const showShell = isHydrated && Boolean(user);

  const closeSidebar = () => setIsSidebarOpen(false);
  const openSidebar = () => setIsSidebarOpen(true);

  const getMainContentMargin = () => {
    if (!isDesktop || !isSidebarOpen) return "lg:ml-0";
    return isCollapsed ? "lg:ml-14" : "lg:ml-64";
  };

  return (
    <div suppressHydrationWarning className="flex min-h-screen flex-col lg:flex-row bg-transparent">
      {showShell ? (
        <>
          {/* Mobile Topbar */}
          {!isDesktop && (
            <div className="sticky top-0 z-30 flex h-14 items-center border-b border-[var(--border)] bg-[var(--surface)] px-3">
              <button
                type="button"
                onClick={openSidebar}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-[var(--muted-foreground)] hover:bg-[var(--surface-muted)]"
                aria-label="Abrir menu"
              >
                <Menu size={20} />
              </button>

              <div className="flex flex-1 items-center justify-center">
                {resolvedLogo ? (
                  <Image
                    src={resolvedLogo}
                    alt={resolvedName}
                    width={130}
                    height={32}
                    unoptimized
                    className="h-8 w-auto max-w-[130px] object-contain"
                  />
                ) : (
                  <span className="text-[15px] font-bold text-[var(--primary)]">
                    {resolvedName}
                  </span>
                )}
              </div>

              <button
                type="button"
                className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-[var(--muted-foreground)] hover:bg-[var(--surface-muted)]"
                aria-label="Notificações"
              >
                <Bell size={18} />
              </button>
            </div>
          )}

          {/* Mobile Overlay */}
          {!isDesktop && isSidebarOpen ? (
            <div
              role="presentation"
              className="fixed inset-0 z-20 bg-slate-900/60 backdrop-blur-sm"
              onClick={closeSidebar}
            />
          ) : null}

          {/* Sidebar Component */}
          <AppSidebar
            isSidebarOpen={isSidebarOpen}
            setIsSidebarOpen={setIsSidebarOpen}
            isDesktop={isDesktop}
            isCollapsed={isCollapsed}
            setIsCollapsed={setIsCollapsed}
          />

          {/* Main Content */}
          <div
            className={`flex-1 p-4 pb-[80px] transition-all duration-300 sm:p-6 md:pb-6 ${getMainContentMargin()}`}
          >
            <main className="text-slate-900 dark:text-slate-100">
              {children}
            </main>
          </div>

          {/* Mobile Bottom Nav */}
          {!isDesktop && (
            <nav className="fixed bottom-0 left-0 right-0 z-40 flex h-[60px] items-stretch border-t border-[var(--border)] bg-[var(--surface)] md:hidden">
              {BOTTOM_NAV.map(({ href, label, Icon, exact }) => {
                const isActive = exact ? pathname === href : pathname?.startsWith(href);
                return (
                  <Link
                    key={href}
                    href={href}
                    className="flex flex-1 flex-col items-center justify-center gap-[3px] text-[10px] font-medium transition-colors"
                    style={{ color: isActive ? "var(--primary)" : "var(--text-muted)" }}
                  >
                    <Icon size={20} strokeWidth={isActive ? 2.2 : 1.7} />
                    <span>{label}</span>
                  </Link>
                );
              })}
            </nav>
          )}

          <WhatsappFloatingInbox />
        </>
      ) : null}
    </div>
  );
}
